import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertTriangle, ArrowDownToLine, ArrowUpToLine, ArrowUpRight, Check, FileText,
  GitBranch, GitFork, Link2, MessageSquare, Pencil, Quote, Scale, ShieldQuestion,
  Sparkles, StickyNote, Trash2, X,
} from "lucide-react";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/cn";
import {
  NODE_REACTIONS, EDGE_REACTIONS, nodeMeta, provenanceMeta, edgeMeta, timeAgo,
} from "@/lib/vocabulary";
import { ATTACHMENT_META, INTENT_META } from "@/components/flow/StrainNode";
import type { AttachmentKind, StrainGraph, GraphNode, GraphEdge, ID, VariantIntent } from "@/types";
import { Badge } from "@/components/Badge";
import { Avatar, EmptyState } from "@/components/AppShell";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { upstreamNodes, downstreamNodes, undercutsOf } from "@/lib/graph";
import { CommentThread } from "@/components/CommentThread";

export function DetailPanel({ graph }: { graph: StrainGraph }) {
  const { selectedNodeId, selectedEdgeId, selectNode, selectEdge } = useStore();
  const node = graph.nodes.find((n) => n.id === selectedNodeId);
  const edge = graph.edges.find((e) => e.id === selectedEdgeId);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--color-border)] shrink-0">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
          {node ? "node detail" : edge ? "connection detail" : "detail"}
        </div>
        {(node || edge) && (
          <button
            onClick={() => { selectNode(null); selectEdge(null); }}
            className="text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
            aria-label="Close panel"
          >
            <X size={15} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {node ? (
            <motion.div
              key={"node-" + node.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.18 }}
            >
              <NodeDetail node={node} graph={graph} />
            </motion.div>
          ) : edge ? (
            <motion.div
              key={"edge-" + edge.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.18 }}
            >
              <EdgeDetail edge={edge} graph={graph} />
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <EmptyState
                icon={<Sparkles size={28} />}
                title="Select a node or edge"
                desc="Click anything in the canvas to inspect its provenance, reactions, comments, and crux status. Double-click empty space to add a node."
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- node detail
function NodeDetail({ node, graph }: { node: GraphNode; graph: StrainGraph }) {
  const store = useStore();
  const toast = useToast((s) => s.push);
  const meta = nodeMeta(node.type);
  const prov = provenanceMeta(node.provenance);
  const Icon = meta.icon;
  const isInferred = node.provenance === "ai_inferred";
  const isCrux = node.badges?.includes("crux") || !!node.crux;

  const upstream = upstreamNodes(node.id, graph.nodes, graph.edges);
  const downstream = downstreamNodes(node.id, graph.nodes, graph.edges);

  const [challengeOpen, setChallengeOpen] = useState(false);
  const [rewordingOpen, setRewordingOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  // a docked modifier knows its anchor via its outgoing link edge
  const attachment = node.attachment;
  const anchorEdge = attachment
    ? graph.edges.find((e) => e.source === node.id && e.target)
    : undefined;
  const anchorNode = anchorEdge?.target
    ? graph.nodes.find((n) => n.id === anchorEdge.target)
    : undefined;

  return (
    <div className="p-4 space-y-4">
      {/* type + provenance + status */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="default" icon={<Icon size={11} />}>{meta.label}</Badge>
        <Badge tone={isInferred ? "inferred" : "primary"} icon={<prov.icon size={11} />}>{prov.label}</Badge>
        {node.badges?.includes("thesis") && <Badge tone="primary">thesis</Badge>}
        {attachment && (
          <Badge tone={attachment.kind === "exception" ? "inferred" : "muted"}>
            docked {attachment.kind}
          </Badge>
        )}
        {isCrux && <Badge tone="amber" icon={<Scale size={11} />}>crux</Badge>}
        {node.status === "needs_evidence" && <Badge tone="warn">needs evidence</Badge>}
        {node.status === "contested" && <Badge tone="danger">contested</Badge>}
        {node.status === "unconfirmed" && <Badge tone="inferred">unconfirmed</Badge>}
      </div>

      {/* docked modifier context: what it is anchored to + promotion path */}
      {attachment && (
        <div
          className="rounded-xl border p-3"
          style={{
            borderColor: `color-mix(in srgb, ${ATTACHMENT_META[attachment.kind].color} 32%, var(--color-border) 68%)`,
            background: `color-mix(in srgb, ${ATTACHMENT_META[attachment.kind].color} 6%, var(--color-surface) 94%)`,
          }}
        >
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ color: ATTACHMENT_META[attachment.kind].color }}>
            {ATTACHMENT_META[attachment.kind].icon} {ATTACHMENT_META[attachment.kind].label} — docked, not a peer node
          </div>
          <p className="mb-2 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">
            {ATTACHMENT_META[attachment.kind].description}
          </p>
          {anchorNode && (
            <button
              onClick={() => store.selectNode(anchorNode.id)}
              className="mb-2 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-left transition-colors hover:border-[var(--color-primary)]"
            >
              <div className="mb-0.5 flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)]">
                <ArrowUpRight size={10} /> anchored to
              </div>
              <div className="text-[12px] text-[var(--color-ink-soft)] line-clamp-2">{anchorNode.text}</div>
            </button>
          )}
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowUpRight size={13} />}
            onClick={() => {
              store.promoteModifier(graph.strain.slug, node.id);
              toast({ tone: "ok", title: "Promoted to full node", desc: "It now stands in the graph on its own — attach evidence to back it." });
            }}
          >
            Promote to full node
          </Button>
        </div>
      )}

      {/* the claim itself — serif, because this is "the idea" */}
      <div>
        <div className="prose-idea text-[var(--color-ink)] text-[15px]">{node.text}</div>
        {node.original_text && node.original_text !== node.text && (
          <div className="mt-2.5 pl-3 border-l-2 border-[var(--color-border)]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)] mb-1">
              <Quote size={10} className="inline mr-1" />original phrasing
            </div>
            <div className="text-[13px] text-[var(--color-ink-muted)] italic font-serif leading-snug">
              “{node.original_text}”
            </div>
          </div>
        )}
      </div>

      {/* competing wordings of this slot */}
      {(node.variants?.length ?? 0) > 0 && (
        <div>
          <div className="mb-1.5 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
            <GitBranch size={11} /> wordings of this slot ({(node.variants?.length ?? 0) + 1})
          </div>
          <div className="space-y-1.5">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5">
              <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-[var(--color-ink-muted)]">
                canonical
                {node.author && <span className="text-[var(--color-ink-faint)]">@{node.author}</span>}
              </div>
              <div className="text-[12px] leading-snug text-[var(--color-ink-soft)]">{node.text}</div>
            </div>
            {node.variants!.map((variant) => {
              const preferred = store.hasReaction(variant.id, "prefer_wording");
              return (
                <div key={variant.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5">
                  <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider">
                    <span style={{ color: INTENT_META[variant.intent].color }}>{INTENT_META[variant.intent].label}</span>
                    {variant.author && <span className="text-[var(--color-ink-faint)]">@{variant.author}</span>}
                    <button
                      onClick={() => store.preferVariant(graph.strain.slug, node.id, variant.id)}
                      className={cn(
                        "ml-auto inline-flex h-5 items-center gap-1 rounded-full border px-1.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
                        preferred
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                          : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-ink)]",
                      )}
                      title="Prefer this wording"
                    >
                      <Scale size={9} /> {variant.prefers} prefer
                    </button>
                  </div>
                  <div className="text-[12px] leading-snug text-[var(--color-ink-soft)]">{variant.text}</div>
                  {variant.note && (
                    <div className="mt-1 text-[10px] text-[var(--color-ink-faint)]">{variant.note}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* crux explanation */}
      {node.crux && (
        <div className="rounded-xl border border-[rgba(251,191,36,0.3)] bg-[rgba(251,191,36,0.06)] p-3">
          <div className="flex items-center gap-1.5 text-[var(--color-secondary)] text-[10px] font-mono uppercase tracking-wider mb-1.5">
            <Scale size={11} /> why this is a crux
          </div>
          <p className="text-[13px] text-[var(--color-ink-soft)] leading-relaxed">{node.crux.reason}</p>
        </div>
      )}

      {/* inferred accept/reject — the "ai is a proposal, not a verdict" rule */}
      {isInferred && (
        <div className="rounded-xl border border-[rgba(167,139,250,0.32)] bg-[rgba(167,139,250,0.06)] p-3">
          <div className="flex items-center gap-1.5 text-[var(--color-inferred)] text-[10px] font-mono uppercase tracking-wider mb-2">
            <Sparkles size={11} /> inferred — not yet accepted
          </div>
          <p className="text-[12px] text-[var(--color-ink-muted)] mb-2.5 leading-relaxed">{prov.note}</p>
          <div className="flex gap-2">
            <Button size="sm" leftIcon={<Check size={13} />} onClick={() => { store.acceptInferredNode(graph.strain.slug, node.id); toast({ tone: "ok", title: "Suggestion accepted", desc: "Node is now confirmed." }); }}>
              Accept
            </Button>
            <Button size="sm" variant="outline" leftIcon={<X size={13} />} onClick={() => { store.rejectInferredNode(graph.strain.slug, node.id); store.selectNode(null); toast({ tone: "info", title: "Suggestion rejected", desc: "Node removed from the strain." }); }}>
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* reaction chips — not likes; belief-shaped signals */}
      <ReactionRow targetId={node.id} chips={NODE_REACTIONS} />

      {/* stats strip */}
      {node.stats && (
        <div className="grid grid-cols-4 gap-2">
          <Stat icon={<MessageSquare size={12} />} value={node.stats.comments} label="comments" />
          <Stat icon={<FileText size={12} />} value={node.stats.evidence} label="evidence" />
          <Stat icon={<GitFork size={12} />} value={node.stats.forks} label="forks" />
          <Stat icon={<Scale size={12} />} value={typeof node.stats.agree === "number" ? `${node.stats.agree}%` : "—"} label="agree" tone="primary" />
        </div>
      )}

      {/* upstream / downstream */}
      <div className="space-y-2">
        <DependencyRow
          icon={<ArrowUpToLine size={13} />}
          label="depends on"
          nodes={upstream}
          onSelect={(id) => store.selectNode(id)}
        />
        <DependencyRow
          icon={<ArrowDownToLine size={13} />}
          label="implies"
          nodes={downstream}
          onSelect={(id) => store.selectNode(id)}
        />
      </div>

      {/* provenance & metadata */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-3 space-y-1.5">
        <MetaRow label="provenance" value={prov.label} note={prov.note} />
        <MetaRow label="author" value={node.author ?? graph.strain.creator} />
        <MetaRow label="confidence" value={node.confidence ? `${Math.round(node.confidence * 100)}%` : "—"} />
        {node.scope && Object.keys(node.scope).length > 0 && (
          <MetaRow label="scope" value={JSON.stringify(node.scope)} />
        )}
      </div>

      {/* actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" leftIcon={<ShieldQuestion size={13} />} onClick={() => setChallengeOpen(true)}>
          Challenge this
        </Button>
        <Button size="sm" variant="outline" leftIcon={<GitFork size={13} />} onClick={() => {
          store.forkStrain(graph.strain.slug, "rebut", "node", `Forked from: ${node.text.slice(0, 60)}`).then(() => {
            toast({ tone: "ok", title: "Fork created", desc: "Edit the fork freely — the diff is tracked." });
          });
        }}>
          Fork from here
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<Pencil size={13} />} onClick={() => setRewordingOpen(true)}>
          Propose a version
        </Button>
        {!attachment && (
          <Button size="sm" variant="ghost" leftIcon={<StickyNote size={13} />} onClick={() => setAttachOpen(true)}>
            Attach exception / caveat
          </Button>
        )}
        <Button size="sm" variant="ghost" leftIcon={<Link2 size={13} />} onClick={() => toast({ tone: "info", title: "Coming soon", desc: "Canonical-node matching arrives with the backend." })}>
          Mark as same
        </Button>
        {node.provenance !== "ai_inferred" && (
          <Button size="sm" variant="ghost" className="text-[var(--color-danger)]" leftIcon={<Trash2 size={13} />} onClick={() => { store.removeNode(graph.strain.slug, node.id); store.selectNode(null); toast({ tone: "info", title: "Node removed" }); }}>
            Delete
          </Button>
        )}
      </div>

      {/* comments anchored to this node */}
      <CommentThread strainId={graph.strain.id} nodeId={node.id} />

      <ChallengeModal
        open={challengeOpen}
        onClose={() => setChallengeOpen(false)}
        graph={graph}
        targetNodeId={node.id}
      />
      <ProposeVariantModal
        open={rewordingOpen}
        onClose={() => setRewordingOpen(false)}
        graph={graph}
        node={node}
      />
      <AttachModifierModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        graph={graph}
        node={node}
      />
    </div>
  );
}

// ----------------------------------------------------------------- edge detail
function EdgeDetail({ edge, graph }: { edge: GraphEdge; graph: StrainGraph }) {
  const store = useStore();
  const toast = useToast((s) => s.push);
  const meta = edgeMeta(edge.relation);
  const Icon = meta.icon;
  const sourceNode = graph.nodes.find((n) => n.id === edge.source);
  const targetNode = edge.target ? graph.nodes.find((n) => n.id === edge.target) : null;
  const targetEdge = edge.target_edge ? graph.edges.find((e) => e.id === edge.target_edge) : null;
  const undercuts = undercutsOf(edge.id, graph.edges);

  const [challengeOpen, setChallengeOpen] = useState(false);

  return (
    <div className="p-4 space-y-4">
      {/* relation */}
      <div className="flex items-center gap-2">
        <Badge tone="default" icon={<Icon size={11} />} style={{ color: meta.color }}>{meta.label}</Badge>
        {edge.provenance && <Badge tone="muted">{edge.provenance}</Badge>}
        {edge.stats?.undercuts ? <Badge tone="amber">✂ {edge.stats.undercuts} undercuts</Badge> : null}
      </div>

      <p className="text-[13px] text-[var(--color-ink-soft)] leading-relaxed">{meta.description}</p>

      {/* from → to */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-3 space-y-2.5">
        {sourceNode && (
          <EdgeEndpoint label="from" node={sourceNode} onClick={() => store.selectNode(sourceNode.id)} />
        )}
        <div className="flex items-center gap-2 pl-1">
          <div className="h-px flex-1" style={{ background: meta.color, opacity: 0.5 }} />
          <Icon size={14} style={{ color: meta.color }} />
          {edge.label && (
            <span className="text-[11px] font-mono text-[var(--color-ink-muted)] uppercase tracking-wider">{edge.label}</span>
          )}
          <div className="h-px flex-1" style={{ background: meta.color, opacity: 0.5 }} />
        </div>
        {targetNode ? (
          <EdgeEndpoint label="to" node={targetNode} onClick={() => store.selectNode(targetNode.id)} />
        ) : targetEdge ? (
          <div className="rounded-lg border border-[rgba(251,191,36,0.3)] bg-[rgba(251,191,36,0.06)] p-2.5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-secondary)] mb-1">
              ⚡ undercuts an inference
            </div>
            <div className="text-[12px] text-[var(--color-ink-muted)]">
              Attacks the connection between{" "}
              <span className="text-[var(--color-ink-soft)]">{graph.nodes.find((n) => n.id === targetEdge.source)?.text.slice(0, 50)}…</span>
              {" "}and the thesis — not either node alone.
            </div>
          </div>
        ) : null}
      </div>

      {/* undercuts against this edge */}
      {undercuts.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1.5 flex items-center gap-1">
            <ShieldQuestion size={11} /> undercuts against this inference ({undercuts.length})
          </div>
          <div className="space-y-1.5">
            {undercuts.map((u) => {
              const un = graph.nodes.find((n) => n.id === u.source);
              return (
                <button
                  key={u.id}
                  onClick={() => un && store.selectNode(un.id)}
                  className="w-full text-left rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2.5 hover:border-[var(--color-undercut)] transition-colors"
                >
                  <div className="text-[12px] text-[var(--color-ink-soft)] line-clamp-2">{un?.text}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* edge reactions */}
      <ReactionRow targetId={edge.id} chips={EDGE_REACTIONS} />

      {/* actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" leftIcon={<ShieldQuestion size={13} />} onClick={() => setChallengeOpen(true)}>
          Challenge this inference
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<Trash2 size={13} />} className="text-[var(--color-danger)]" onClick={() => { store.removeEdge(graph.strain.slug, edge.id); store.selectEdge(null); toast({ tone: "info", title: "Connection removed" }); }}>
          Delete
        </Button>
      </div>

      <CommentThread strainId={graph.strain.id} edgeId={edge.id} />

      <ChallengeModal
        open={challengeOpen}
        onClose={() => setChallengeOpen(false)}
        graph={graph}
        targetEdgeId={edge.id}
      />
    </div>
  );
}

// ----------------------------------------------------------------- sub-pieces
function ReactionRow({
  targetId, chips,
}: {
  targetId: ID;
  chips: readonly { type: string; label: string; emoji: string; tone: string }[];
}) {
  const { hasReaction, toggleReaction } = useStore();
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1.5">react</div>
      <div className="flex flex-wrap gap-1">
        {chips.map((c) => {
          const active = hasReaction(targetId, c.type as any);
          return (
            <button
              key={c.type}
              onClick={() => toggleReaction(targetId, c.type as any)}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border transition-all",
                active
                  ? "border-[var(--color-primary)] bg-[rgba(94,234,212,0.12)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:border-[var(--color-border-bright)]",
              )}
            >
              <span className="text-[10px]">{c.emoji}</span>
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ icon, value, label, tone }: { icon: React.ReactNode; value: React.ReactNode; label: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-2 text-center">
      <div className={cn("flex items-center justify-center gap-0.5 text-[var(--color-ink)] text-sm font-mono font-semibold", tone === "primary" && "text-[var(--color-primary)]")}>
        {icon}{value}
      </div>
      <div className="text-[9px] text-[var(--color-ink-faint)] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function DependencyRow({
  icon, label, nodes, onSelect,
}: {
  icon: React.ReactNode; label: string; nodes: GraphNode[]; onSelect: (id: ID) => void;
}) {
  if (nodes.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1.5 flex items-center gap-1">
        {icon} {label} ({nodes.length})
      </div>
      <div className="space-y-1">
        {nodes.slice(0, 4).map((n) => (
          <button
            key={n.id}
            onClick={() => onSelect(n.id)}
            className="w-full text-left rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-2 hover:border-[var(--color-primary)] transition-colors"
          >
            <div className="text-[12px] text-[var(--color-ink-soft)] line-clamp-2">{n.text}</div>
          </button>
        ))}
        {nodes.length > 4 && <div className="text-[11px] text-[var(--color-ink-faint)] pl-1">+{nodes.length - 4} more</div>}
      </div>
    </div>
  );
}

function EdgeEndpoint({ label, node, onClick }: { label: string; node: GraphNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-left rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-2.5 hover:border-[var(--color-primary)] transition-colors">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)] mb-0.5">{label}</div>
      <div className="text-[12px] text-[var(--color-ink-soft)] line-clamp-2">{node.text}</div>
    </button>
  );
}

function MetaRow({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex items-baseline gap-2 text-[12px]">
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-ink-faint)] w-20 shrink-0">{label}</span>
      <span className="text-[var(--color-ink-soft)]" title={note}>{value}</span>
    </div>
  );
}

// ----------------------------------------------------------------- challenge modal
export function ChallengeModal({
  open, onClose, graph, targetNodeId, targetEdgeId,
}: {
  open: boolean; onClose: () => void; graph: StrainGraph;
  targetNodeId?: ID; targetEdgeId?: ID;
}) {
  const addComment = useStore((s) => s.addComment);
  const toast = useToast((s) => s.push);
  const [type, setType] = useState("challenge_claim");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const types = [
    { v: "challenge_claim", label: "Challenge the claim" },
    { v: "challenge_inference", label: "Challenge the inference" },
    { v: "challenge_source", label: "Challenge the source" },
    { v: "challenge_definition", label: "Challenge a definition" },
    { v: "challenge_scope", label: "Challenge the scope" },
    { v: "challenge_criterion", label: "Challenge the criterion" },
    { v: "stronger_replacement", label: "Propose a stronger replacement" },
    { v: "bridge_reformulation", label: "Propose a bridge" },
  ];

  const commit = () => {
    if (!body.trim()) return;
    addComment({
      strain_id: graph.strain.id,
      version_id: graph.version.id,
      node_id: targetNodeId,
      edge_id: targetEdgeId,
      comment_type: "challenge",
      body: `${title ? title + " — " : ""}${body}`,
      parent_comment_id: null,
    });
    toast({ tone: "ok", title: "Challenge posted", desc: "Anchored to the selected point. Others can react or fork from it." });
    setTitle(""); setBody("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Challenge this"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={commit} disabled={!body.trim()}>Post challenge</Button></>}
    >
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Challenge type</label>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            {types.map((t) => (
              <button
                key={t.v}
                onClick={() => setType(t.v)}
                className={cn(
                  "text-left px-2 py-2 rounded-lg border text-[11px] transition-colors",
                  type === t.v
                    ? "border-[var(--color-primary)] bg-[var(--color-surface-2)] text-[var(--color-ink)]"
                    : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Title (optional)</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's the crux of your challenge?" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)]" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">Your challenge</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Be precise — name the specific claim, inference, or assumption you're pressing on." className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)] resize-none" />
        </div>
        <p className="text-[11px] text-[var(--color-ink-faint)]">
          The durable object is the map, not the fight. Your challenge stays anchored to this point and can be forked into a new version.
        </p>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------- propose-variant modal
// proposing a wording never overwrites the canonical text: it creates a
// variant in the slot that everyone can browse, diff, and prefer.
function ProposeVariantModal({
  open, onClose, graph, node,
}: { open: boolean; onClose: () => void; graph: StrainGraph; node: GraphNode }) {
  const addVariant = useStore((s) => s.addVariant);
  const toast = useToast((s) => s.push);
  const [intent, setIntent] = useState<VariantIntent>("rewording");
  const [text, setText] = useState(node.text);
  const [note, setNote] = useState("");

  const intents = Object.keys(INTENT_META) as VariantIntent[];

  const commit = async () => {
    if (!text.trim() || text.trim() === node.text) return;
    await addVariant(graph.strain.slug, node.id, { intent, text: text.trim(), note: note.trim() || undefined });
    toast({ tone: "ok", title: "Version proposed", desc: "It joins the slot's version rail — the canonical wording is untouched." });
    setText(node.text); setNote("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Propose a version"
      footer={
        <>
          <Button variant="ghost" onClick={() => { setText(node.text); onClose(); }}>Cancel</Button>
          <Button onClick={commit} disabled={!text.trim() || text.trim() === node.text}>Propose</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)] mb-1">canonical wording</div>
          <div className="text-[13px] text-[var(--color-ink-soft)]">{node.text}</div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">intent</label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {intents.map((item) => (
              <button
                key={item}
                onClick={() => setIntent(item)}
                className={cn(
                  "rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors",
                  intent === item
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                    : "border-[var(--color-border)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
                )}
                style={{ color: intent === item ? INTENT_META[item].color : undefined }}
              >
                {INTENT_META[item].label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">your wording</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)] resize-none" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">what changed (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. narrows the population to students without a clear track" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)]" />
        </div>
        <p className="text-[11px] text-[var(--color-ink-faint)]">
          Versions never overwrite the canonical wording. They sit in the slot's rail where anyone can compare and prefer them.
        </p>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------- attach-modifier modal
function AttachModifierModal({
  open, onClose, graph, node,
}: { open: boolean; onClose: () => void; graph: StrainGraph; node: GraphNode }) {
  const addModifier = useStore((s) => s.addModifier);
  const toast = useToast((s) => s.push);
  const [kind, setKind] = useState<AttachmentKind>("exception");
  const [text, setText] = useState("");

  const commit = async () => {
    if (!text.trim()) return;
    await addModifier(graph.strain.slug, node.id, kind, text.trim());
    toast({ tone: "ok", title: `${kind === "exception" ? "Exception" : "Caveat"} attached`, desc: "It docks onto the node — visible as a chip, expanded on focus." });
    setText("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Attach an exception or caveat"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={commit} disabled={!text.trim()}>Attach</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(ATTACHMENT_META) as AttachmentKind[]).map((item) => (
            <button
              key={item}
              onClick={() => setKind(item)}
              className={cn(
                "rounded-lg border p-2.5 text-left transition-colors",
                kind === item
                  ? "border-[var(--color-primary)] bg-[var(--color-surface-2)]"
                  : "border-[var(--color-border)] hover:bg-[var(--color-surface-2)]",
              )}
            >
              <div className="mb-0.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider" style={{ color: ATTACHMENT_META[item].color }}>
                {ATTACHMENT_META[item].icon} {ATTACHMENT_META[item].label}
              </div>
              <div className="text-[11px] leading-snug text-[var(--color-ink-muted)]">{ATTACHMENT_META[item].description}</div>
            </button>
          ))}
        </div>
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-faint)] mb-1">attaches to</div>
          <div className="text-[13px] text-[var(--color-ink-soft)] line-clamp-2">{node.text}</div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">the {kind}</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={kind === "exception" ? "e.g. Licensed professions are clear exceptions because the degree is legally required." : "e.g. This is an average; it hides variance by region and completion."}
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-sm text-[var(--color-ink)] resize-none"
          />
        </div>
        <p className="text-[11px] text-[var(--color-ink-faint)]">
          Modifiers stay docked to their node instead of becoming peer cards. If one accumulates its own support, promote it to a full node from its detail panel.
        </p>
      </div>
    </Modal>
  );
}
